// Lightweight, dependency-free chart (inline SVG) for the dashboard widgets.
// Two modes share one data shape (labels + series):
//   - 'stacked-bars': one bar per label, series stacked (e.g. revenue per brand)
//   - 'lines':        one line per series (e.g. last 3 years overlaid by month)

import { ReactNode } from 'react'
import { CHART_COLORS, CHART_GRID_COLOR, CHART_AXIS_TEXT_COLOR, CHART_ZERO_LINE_COLOR, CHART_MAX_X_LABELS } from '../../shared/constants'

export interface ChartSeries { name: string, values: number[], color: string }
export interface ChartProps {
    mode: 'stacked-bars' | 'lines'
    labels: string[]
    series: ChartSeries[]
    height?: number
    formatValue?: (n: number) => string
    aside?: ReactNode // extra content shown in the right column, below the legend
}

// Default colour palette (brands / years). Extends by cycling if needed.
export { CHART_COLORS }

const W = 600
const PAD = { left: 52, right: 12, top: 10, bottom: 26 }

function niceMax(v: number): number {
    if (v <= 0) return 1
    const pow = Math.pow(10, Math.floor(Math.log10(v)))
    const n = v / pow
    const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10
    return step * pow
}

export default function Chart({ mode, labels, series, height = 240, formatValue, aside }: ChartProps) {
    const H = height
    const x0 = PAD.left, x1 = W - PAD.right, y0 = PAD.top, y1 = H - PAD.bottom
    const fmt = formatValue ?? ((n: number) => String(n))
    const n = labels.length

    // Y domain: stacked bars are always ≥ 0; lines (e.g. balance) may go negative,
    // so include a zero baseline and a nice negative minimum.
    const colSum = (i: number) => series.reduce((s, ser) => s + (ser.values[i] ?? 0), 0)
    const lineVals = mode === 'lines' ? series.flatMap((s) => s.values) : []
    const rawMax = mode === 'stacked-bars'
        ? Math.max(0, ...labels.map((_, i) => colSum(i)))
        : Math.max(0, ...lineVals)
    const rawMin = mode === 'lines' ? Math.min(0, ...lineVals) : 0
    const domainMax = niceMax(rawMax)
    const domainMin = rawMin < 0 ? -niceMax(-rawMin) : 0
    const span = (domainMax - domainMin) || 1

    const yOf = (v: number) => y1 - ((v - domainMin) / span) * (y1 - y0)
    const colW = n > 0 ? (x1 - x0) / n : 0
    const cx = (i: number) => x0 + colW * (i + 0.5)
    const barW = Math.min(colW * 0.7, 46)

    // Show ~12 x-labels max so day-granularity charts stay readable.
    const labelStep = Math.max(1, Math.ceil(n / CHART_MAX_X_LABELS))
    const gridTicks = [0, 0.25, 0.5, 0.75, 1]

    return (
        <div className="chart">
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet" role="img">
                {/* Y grid + labels */}
                {gridTicks.map((t) => {
                    const v = domainMin + span * t, y = yOf(v)
                    return (
                        <g key={t}>
                            <line x1={x0} y1={y} x2={x1} y2={y} stroke={CHART_GRID_COLOR} strokeWidth={1} />
                            <text x={x0 - 6} y={y + 3} textAnchor="end" fontSize={9} fill={CHART_AXIS_TEXT_COLOR}>{fmt(v)}</text>
                        </g>
                    )
                })}
                {/* Zero baseline (only when the domain spans negatives) */}
                {domainMin < 0 && <line x1={x0} y1={yOf(0)} x2={x1} y2={yOf(0)} stroke={CHART_ZERO_LINE_COLOR} strokeWidth={1} />}

                {/* X labels */}
                {labels.map((lab, i) => (i % labelStep === 0 ? (
                    <text key={i} x={cx(i)} y={H - 8} textAnchor="middle" fontSize={9} fill={CHART_AXIS_TEXT_COLOR}>{lab}</text>
                ) : null))}

                {mode === 'stacked-bars'
                    ? labels.map((_, i) => {
                        let acc = 0
                        return (
                            <g key={i}>
                                {series.map((ser) => {
                                    const v = ser.values[i] ?? 0
                                    if (v <= 0) return null
                                    const yTop = yOf(acc + v), yBot = yOf(acc)
                                    acc += v
                                    return (
                                        <rect key={ser.name} x={cx(i) - barW / 2} y={yTop} width={barW} height={Math.max(0, yBot - yTop)} fill={ser.color}>
                                            <title>{`${labels[i]} · ${ser.name}: ${fmt(v)}`}</title>
                                        </rect>
                                    )
                                })}
                            </g>
                        )
                    })
                    : series.map((ser) => {
                        const pts = ser.values.map((v, i) => `${cx(i)},${yOf(v ?? 0)}`).join(' ')
                        return (
                            <g key={ser.name}>
                                <polyline points={pts} fill="none" stroke={ser.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                                {ser.values.map((v, i) => (
                                    <circle key={i} cx={cx(i)} cy={yOf(v ?? 0)} r={2.5} fill={ser.color}>
                                        <title>{`${labels[i]} · ${ser.name}: ${fmt(v ?? 0)}`}</title>
                                    </circle>
                                ))}
                            </g>
                        )
                    })}
            </svg>
            {(series.length > 1 || aside) && (
                <div className="chart-side">
                    {series.length > 1 && (
                        <div className="chart-legend">
                            {series.map((s) => (
                                <span key={s.name} className="chart-legend-item">
                                    <span className="chart-legend-dot" style={{ background: s.color }} />{s.name}
                                </span>
                            ))}
                        </div>
                    )}
                    {aside && <div className="chart-aside">{aside}</div>}
                </div>
            )}
        </div>
    )
}
